package dev.kuml.portal.web.editor

import kotlin.math.abs

/** [PresenceScheduler]'s pure input state -- see that object's class KDoc. */
public data class SchedulerState(
    val lastSentAtMs: Double = Double.NEGATIVE_INFINITY,
    val lastSentCursor: Pair<Double, Double>? = null,
)

/** What [PresenceScheduler] decided for one event -- see each function's own KDoc for which
 * variant it can return. */
public sealed interface SchedulerDecision {
    /** Send a presence frame right now -- [nextState] is the [SchedulerState] to keep afterwards. */
    public data class SendNow(
        val nextState: SchedulerState,
    ) : SchedulerDecision

    /** Don't send yet -- arm a trailing-edge timer for [delayMs] from now (a no-op if one is
     * already armed, see `EditorScreen.kt`'s `pendingPresenceTimer`). The payload actually sent when
     * that timer fires is built from whatever is CURRENT at fire time, not frozen here -- see this
     * object's class KDoc "a single scheduler for everything". */
    public data class ScheduleTrailing(
        val delayMs: Double,
    ) : SchedulerDecision

    /** Nothing to do -- the movement was too small to matter (see [PresenceScheduler.onPointerMove]). */
    public object Suppress : SchedulerDecision
}

/**
 * R-28: the single rate-limiting slot ALL outgoing presence traffic shares -- pointer moves,
 * selection changes, and pointer-leave/idle payloads alike (a deliberate Jobs-review correction: an
 * earlier draft let a selection change bypass the movement throttle, which [onSelectionChange] now
 * explicitly does not). No event kind can ever push more than 10 presence frames per second out of
 * one tab, full stop -- matches the server's own `collabPresenceRateLimitMaxAttempts` budget math
 * (300 attempts / 10s window == 3 tabs at this object's own 10 Hz cap, see
 * `kuml-portal-server`'s `PortalServerConfig.kt`).
 *
 * Pure and DOM-free -- every side effect (the actual `CollabClient.sendPresence` call, the
 * `window.setTimeout` trailing-edge timer) lives in `EditorScreen.kt`, which holds [SchedulerState]
 * as a plain closure-captured `var` exactly like every other piece of that screen's mutable state.
 */
public object PresenceScheduler {
    /** The shared slot width -- see class KDoc. Exactly 10 Hz. */
    public const val MIN_INTERVAL_MS: Double = 100.0

    /** [onPointerMove] suppresses anything under this Manhattan distance (in the same viewBox units
     * [PresencePayload]'s `c` field carries) -- a still, or near-still, pointer should not keep
     * resetting the 100ms slot forever. */
    public const val MIN_MOVEMENT_UNITS: Double = 2.0

    /** `EditorScreen.kt`'s idle-timer duration -- a pointer that stops moving for this long without
     * a `pointerleave` is treated exactly like one (see `PresenceState`'s cursor-TTL KDoc for why a
     * separate, longer TTL also exists on the RECEIVING side). */
    public const val IDLE_TIMEOUT_MS: Double = 5_000.0

    /**
     * A raw pointer-move sample at viewBox position [point]. [SchedulerDecision.Suppress] if the
     * Manhattan distance from [SchedulerState.lastSentCursor] is under [MIN_MOVEMENT_UNITS] --
     * always treated as "moved enough" when there is no previously sent cursor at all (e.g. the
     * very first move after a reconnect). Otherwise [SchedulerDecision.SendNow] if at least
     * [MIN_INTERVAL_MS] has elapsed since [SchedulerState.lastSentAtMs], else
     * [SchedulerDecision.ScheduleTrailing] for the remaining time in the current slot.
     */
    public fun onPointerMove(
        state: SchedulerState,
        now: Double,
        point: Pair<Double, Double>,
    ): SchedulerDecision {
        val last = state.lastSentCursor
        val movement = if (last == null) Double.MAX_VALUE else abs(point.first - last.first) + abs(point.second - last.second)
        if (movement < MIN_MOVEMENT_UNITS) return SchedulerDecision.Suppress
        val elapsed = now - state.lastSentAtMs
        return if (elapsed >= MIN_INTERVAL_MS) {
            SchedulerDecision.SendNow(state.copy(lastSentAtMs = now, lastSentCursor = point))
        } else {
            SchedulerDecision.ScheduleTrailing(MIN_INTERVAL_MS - elapsed)
        }
    }

    /**
     * A local selection change -- belongs to the SAME 100ms slot as [onPointerMove] (Jobs-review
     * correction: a selection change is never allowed to bypass the shared throttle just because it
     * is a rarer, "more important" event than a cursor move). Never [SchedulerDecision.Suppress] --
     * a selection change always eventually sends, it just may have to wait out the current slot.
     */
    public fun onSelectionChange(
        state: SchedulerState,
        now: Double,
    ): SchedulerDecision {
        val elapsed = now - state.lastSentAtMs
        return if (elapsed >= MIN_INTERVAL_MS) {
            SchedulerDecision.SendNow(state.copy(lastSentAtMs = now))
        } else {
            SchedulerDecision.ScheduleTrailing(MIN_INTERVAL_MS - elapsed)
        }
    }

    /**
     * A `pointerleave` or the [IDLE_TIMEOUT_MS] idle timer firing -- ALWAYS
     * [SchedulerDecision.SendNow], immediately, regardless of the current slot: unlike a continuous
     * stream of pointer moves, this fires at most once per departure/idle period, so bypassing the
     * throttle here can never approach the 10 Hz budget the class KDoc guarantees.
     * [SchedulerState.lastSentCursor] resets to `null` in the returned state, so the very next
     * pointer move after this is always treated as "moved enough" by [onPointerMove].
     */
    public fun onPointerLeaveOrIdle(
        state: SchedulerState,
        now: Double,
    ): SchedulerDecision = SchedulerDecision.SendNow(state.copy(lastSentAtMs = now, lastSentCursor = null))
}
